import { useMemo } from "react";
import Chart from "react-apexcharts";

import { formatMetricOrDimensionName } from "~/pages/GoogleDashboardPage/utils/formatMetricOrDimensionName";
import { useChartTheme } from "~/pages/GoogleDashboardPage/utils/useChartTheme";

import type {
  GoogleAnalyticsPropertyRealtimeReport,
  GoogleAnalyticsPropertyReport,
} from "../../../../types/RenderableWidget.type";
import { formatDimensionValue } from "../../utils/formatDimensionValue";
import { formatMetricValue } from "../../utils/formatMetricValue";
import styles from "./PieChartView.module.scss";

export const PieChartView = ({
  data,
}: {
  data: GoogleAnalyticsPropertyReport | GoogleAnalyticsPropertyRealtimeReport;
}) => {
  const chartTheme = useChartTheme();

  if (!data.dimension_headers || data.dimension_headers.length === 0) {
    throw new Error("Pie chart requires at least one dimension");
  }

  if (!data.metric_headers || data.metric_headers.length === 0) {
    throw new Error("Pie chart requires at least one metric");
  }

  // For pie charts, we typically use the first dimension for labels and first metric for values
  const labels = useMemo(
    () =>
      (data.rows || []).map((row) => {
        if (!row.dimension_values || row.dimension_values.length === 0) {
          return "Unknown";
        }

        return formatDimensionValue(
          row.dimension_values[0].value,
          data.dimension_headers![0].name,
        );
      }),
    [data.rows, data.dimension_headers],
  );

  const series = useMemo(
    () =>
      (data.rows || []).map((row) => {
        const metricType = data.metric_headers[0].type;
        if (!(metricType in PARSERS)) {
          throw new Error(
            `Pie chart does not support metrics of ${metricType} type`,
          );
        }

        return PARSERS[metricType](row.metric_values[0].value);
      }),
    [data.rows, data.metric_headers],
  );

  // Check if the metric is float type for proper formatting
  const hasFloatMetrics = data.metric_headers.some(
    ({ type }) => type === "TYPE_FLOAT" || type === "TYPE_PERCENT",
  );

  const metricName = formatMetricOrDimensionName(data.metric_headers[0].name);

  const options = useMemo(() => {
    const formatMetricNumber = (n: number) =>
      formatMetricValue(
        n.toString(),
        hasFloatMetrics ? "TYPE_FLOAT" : "TYPE_INTEGER",
      );

    return {
      chart: {
        type: "donut" as const,
        toolbar: { show: false },
        fontFamily: chartTheme.fontFamily,
        // See LineChartView for the rationale. Donuts are particularly
        // sensitive — every parent-resize redraw would re-spin the
        // slices into place.
        animations: {
          enabled: true,
          dynamicAnimation: { enabled: false },
        },
      },
      colors: chartTheme.palette,
      // White stroke between slices, the same colour as the card
      // background, so the slices appear visually separated against
      // the card without a hard outline.
      stroke: {
        show: true,
        width: 3,
        colors: [chartTheme.bgCard],
      },
      // Hide on-slice percentage labels — they overlap on small slices
      // and the legend + tooltip + centre label cover the same info
      // more legibly. Modern donut charts almost never show them.
      dataLabels: { enabled: false },
      labels,
      tooltip: {
        theme: chartTheme.theme,
        y: {
          formatter: (value: number) => formatMetricNumber(value),
          title: {
            formatter: () => metricName,
          },
        },
      },
      legend: {
        position: "bottom" as const,
        fontFamily: chartTheme.fontFamily,
        fontSize: "12px",
        labels: { colors: chartTheme.textGrey },
        formatter: formatMetricOrDimensionName,
        itemMargin: { horizontal: 8, vertical: 4 },
        markers: {
          size: 6,
          shape: "circle" as const,
        },
      },
      plotOptions: {
        pie: {
          // Donut style with the centre showing the metric name and
          // total. On hover, the centre flips to the slice's label
          // and value (Apex handles this automatically).
          donut: {
            size: "68%",
            labels: {
              show: true,
              name: {
                show: true,
                fontFamily: chartTheme.fontFamily,
                fontSize: "12px",
                color: chartTheme.textGrey,
                offsetY: -4,
              },
              value: {
                show: true,
                fontFamily: chartTheme.fontFamily,
                fontSize: "20px",
                fontWeight: 600,
                color: chartTheme.textBlack,
                offsetY: 4,
                // Apex passes the value as a pre-stringified number;
                // re-parse and run it through our normal formatter so
                // integer/float/percent metrics each render correctly.
                formatter: (val: string) => formatMetricNumber(parseFloat(val)),
              },
              total: {
                show: true,
                showAlways: false,
                label: metricName,
                fontFamily: chartTheme.fontFamily,
                fontSize: "12px",
                color: chartTheme.textGrey,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formatter: (w: any) => {
                  const totals = w?.globals?.seriesTotals as
                    | number[]
                    | undefined;
                  if (!totals) return "";
                  const sum = totals.reduce((a, b) => a + b, 0);

                  return formatMetricNumber(sum);
                },
              },
            },
          },
          expandOnClick: false,
        },
      },
    };
  }, [chartTheme, labels, hasFloatMetrics, metricName]);

  return (
    <Chart
      className={styles.root}
      options={options}
      series={series}
      type="donut"
      height="100%"
    />
  );
};

const PARSERS: Record<string, (value: string) => number> = {
  TYPE_INTEGER: parseInt,
  TYPE_FLOAT: parseFloat,
  TYPE_PERCENT: (value: string) => parseFloat(value) * 100,
};
